import React, { useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import Loading from "components/loading/loading.component";
import { IPaymentState } from "interfaces/PaymentInterface";
import Input from "components/input/input.component";
import Button from "components/button/button";
import { emailRegex } from "constants/regex";

const PaymentForm: React.FC<IPaymentState> = ({
  isLoading,
  onGoback,
  onSuccess,
  email,
  isGift,
  recipientEmails,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [message, setMessage] = useState<string>("");
  const [billingEmail, setBillingEmail] = useState<string>(email || "");
  const [emailError, setEmailError] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);

  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError("Email is required");
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateEmail(billingEmail)) return;
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        receipt_email: billingEmail,
      },
    });

    if (error?.type === "card_error" || error?.type === "validation_error") {
      setMessage(error.message as string);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      navigate("/register-success", {
        state: { isGift: !!isGift, recipientEmails: recipientEmails ?? [] },
      });
    } else {
      setMessage("An unexpected error occurred.");
    }

    setIsProcessing(false);
  };

  return (
    <>
      {isLoading ? (
        <Loading text="Loading payment gateway..." />
      ) : (
        <form id="payment-form" onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <Input
              id="billing-email"
              name="email"
              placeholder="Email Address"
              value={billingEmail}
              showLabel={true}
              label="Email"
              type="text"
              hasError={!!emailError}
              errorMessage={emailError}
              onChange={(e) => {
                setBillingEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              onBlur={() => validateEmail(billingEmail)}
            />
          </div>
          <div className="mb-6">
            <PaymentElement id="payment-element" onReady={() => setIsElementReady(true)} />
          </div>
          <div className="flex w-full gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onGoback}
              className="w-1/3"
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isProcessing || !stripe || !elements || !isElementReady}
              className="w-2/3"
            >
              {isProcessing ? "Processing..." : "Confirm Payment"}
            </Button>
          </div>

          {message && (
            <div className="mt-4 text-error text-body-sm">
              {message}
            </div>
          )}
        </form>
      )}
    </>
  );
};

export default PaymentForm;
